import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone } from 'lucide-react';

import { PageSeo } from '../components/PageSeo';
import { SocialIconFacebook, SocialIconX } from '../components/SocialBrandIcons';
import { SubpageLayout } from '../components/SubpageLayout';
import {
    CONTACT_EMAIL,
    CONTACT_MAP_EMBED_SRC,
    CONTACT_MAP_EMBED_SRC_EN,
    CONTACT_PHONE_DISPLAY,
    CONTACT_PHONE_HREF,
    mapContactSocialGroupsFromI18n,
} from '../data/contactPageContent';

/**
 * İletişim — URL: /contact
 */
export function IletisimPage() {
    const { t, i18n } = useTranslation();

    const socialGroups = useMemo(() => {
        const raw = t('contactPage.socialGroups', { returnObjects: true });
        return mapContactSocialGroupsFromI18n(raw);
    }, [t]);

    const mapSrc = i18n.language?.toLowerCase().startsWith('en')
        ? CONTACT_MAP_EMBED_SRC_EN
        : CONTACT_MAP_EMBED_SRC;

    return (
        <>
            <PageSeo
                title={`${t('contactPage.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.contact')}
            />
            <SubpageLayout
                title={t('contactPage.title')}
                subtitle={t('contactPage.subtitle')}
                contentClassName="contact-page"
            >
            <div className="contact-panel mb-4 mb-lg-5">
                <h2 className="contact-panel-heading h5 mb-4">
                    {t('contactPage.panelHeading')}
                </h2>
                <dl className="contact-details mb-4">
                    <div className="contact-detail-row">
                        <dt className="contact-detail-label">
                            <Phone
                                size={18}
                                strokeWidth={1.75}
                                className="contact-detail-icon"
                                aria-hidden
                            />
                            {t('contactPage.phoneLabel')}
                        </dt>
                        <dd className="contact-detail-value mb-0">
                            <a
                                href={CONTACT_PHONE_HREF}
                                className="contact-detail-link"
                            >
                                {CONTACT_PHONE_DISPLAY}
                            </a>
                        </dd>
                    </div>
                    <div className="contact-detail-row">
                        <dt className="contact-detail-label">
                            <Mail
                                size={18}
                                strokeWidth={1.75}
                                className="contact-detail-icon"
                                aria-hidden
                            />
                            {t('contactPage.emailLabel')}
                        </dt>
                        <dd className="contact-detail-value mb-0">
                            <a
                                href={`mailto:${CONTACT_EMAIL}`}
                                className="contact-detail-link"
                            >
                                {CONTACT_EMAIL}
                            </a>
                        </dd>
                    </div>
                </dl>

                <h3 className="contact-panel-heading h6 mb-3 mt-4 pt-2">
                    {t('contactPage.groupsHeading')}
                </h3>
                <ul className="contact-groups-list list-unstyled mb-0">
                    {socialGroups.map((group) => (
                        <li key={group.id} className="contact-groups-item">
                            <a
                                href={group.href}
                                className="contact-groups-link"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {group.network === 'facebook' ? (
                                    <SocialIconFacebook
                                        className="contact-groups-brand-icon"
                                        size={36}
                                    />
                                ) : (
                                    <SocialIconX
                                        className="contact-groups-brand-icon"
                                        size={36}
                                    />
                                )}
                                <span className="contact-groups-label">
                                    {group.label}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="contact-map-wrap">
                <iframe
                    className="contact-map-iframe"
                    src={mapSrc}
                    title={t('contactPage.mapIframeTitle')}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                />
            </div>
        </SubpageLayout>
        </>
    );
}
